import { useTheme } from "@hooks/useTheme";
import { ScanToolSearchInfoContext } from "@components/Guards/ScanToolGuard";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Communication from "../../../components/Communication/Communication";
import AnalysisIcon from "../../../components/Icons/AnalysisIcon";
import CheckFollowUpIcon from "../../../components/Icons/CheckFollowUpIcon";
import FollowUpIcon from "../../../components/Icons/FollowUpIcon";
import ReadyIcon from "../../../components/Icons/ReadyIcon";
import ReviewIcon from "../../../components/Icons/ReviewIcon";
import UsageTypeCheckIcon from "../../../components/Icons/UsageTypeCheckIcon";
import UsageTypeInputIcon from "../../../components/Icons/UsageTypeInputIcon";
import UsageTypeReviewIcon from "../../../components/Icons/UsageTypeReviewIcon";
import { ComponentTable, ComponentTableHeader } from "../../../components/Tables";
import {
  BasicInfoSection,
  Breadcrumb,
  FulfillmentInfo,
  VerificationReportInfo,
  WorkflowProgressClarity,
} from "../../../components/UI";
import queryKeys from "../../../configs/react-query/query-key";
import { PermissionContext } from "../../../contexts/PermissionContext";
import { useClarityVerification } from "../../../hooks/useClarityVerification";
import { useFetchData } from "../../../hooks/useFetchData";
import { useMutationData } from "../../../hooks/useMutationData";
import useProjectMembers from "../../../hooks/useProjectMembers";
import { useNavigate } from "react-router-dom";
import { formatFullDateTime } from "@utils/common";
import { getLocalStorage } from "@utils/local-storage";
import { LOCAL_STORAGE } from "@constants/index";

const ClarityVerification = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { projectSeq, binaryVerificationSeq } = useParams();
  const [modifiedComponentData, setModifiedComponentData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [requests, setRequests] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const { creatorList = [] } = useContext(ScanToolSearchInfoContext) ?? {};
  const { hasDeveloperPermission, hasManagerPermission, hasSuperUserPermission, hasManagePermission, permissions } =
    useContext(PermissionContext);
  const queryClient = useQueryClient();
  const { membersData: projectMembers } = useProjectMembers(projectSeq);
  const navigate = useNavigate();

  // Set preference for Clarity tab when this component mounts
  useEffect(() => {
    // Safely set preference when component mounts
    try {
      localStorage.setItem("preferredTab", "clarity");
      localStorage.setItem("lastProjectSeq", projectSeq);
    } catch (e) {
      console.error("Error setting localStorage items:", e);
    }

    return () => {
      // This will run when component unmounts (including browser back)
      try {
        localStorage.setItem("preferredTab", "clarity");
        localStorage.setItem("lastProjectSeq", projectSeq);
      } catch (e) {
        console.error("Error setting localStorage items on unmount:", e);
      }
    };
  }, [projectSeq]);

  const { data: requestList } = useFetchData(
    [queryKeys.binaryAction, binaryVerificationSeq],
    "/binary_action",
    {
      method: "GET",
      params: {
        verificationSeq: binaryVerificationSeq,
      },
    },
    {},
    {
      enabled: !!binaryVerificationSeq,
    },
  );

  const completeAction = useMutationData(
    "/binary_action_detail",
    {
      method: "POST",
      params: {
        actionType: "complete",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error completing action:", error);
      },
    },
  );

  const approveAction = useMutationData(
    "/binary_action_detail",
    {
      method: "POST",
      params: {
        actionType: "approve",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error approving action:", error);
      },
    },
  );

  const rejectAction = useMutationData(
    "/binary_action_detail",
    {
      method: "POST",
      params: {
        actionType: "reject",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error rejecting action:", error);
      },
    },
  );

  const createActionRequest = useMutationData(
    "/binary_action_request",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error creating action request:", error);
      },
    },
  );

  const addVerificationComment = useMutationData(
    "/binary_verification_verification_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding verification comment:", error);
      },
    },
  );

  const addFulfillmentComment = useMutationData(
    "/binary_verification_fulfillment_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding fulfillment comment:", error);
      },
    },
  );

  const addUsageComment = useMutationData(
    "/binary_verification_usage_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding usage comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addUsageReviewComment = useMutationData(
    "/binary_usage_type_review_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding usage review comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addUsageInputComment = useMutationData(
    "/binary_usage_type_input_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding usage input comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addFollowupCheckComment = useMutationData(
    "/binary_check_follow_up_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.binaryAction]);
      },
      onError: (error) => {
        console.error("Error adding followup check comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const {
    // Loading state
    isLoading,
    isFetching,

    verificationStatus,
    projectData,
    verificationData,
    fetchedLicensePolicy,
    fetchedBinaryScanComponent,
    fetchedBinaryScanFollowupPlan,
    binaryScanFollowupPlanUpdatedAt,
    projectId,
    verificationVersion,
    handleMoveToReview,
    handleMoveToDone,
    isReviewOrDoneStage,
    isNotAnalysis,
    handleMoveToFulfillment,
    fulfillmentInfoRef,
    handleGetVerificationReportForPopup,
    verificationReportsData,
    handleToRequestReVerification,
    handleReUploadFile,
    handleAddVerificationReport,
    isFulfillmentOrCheckFulfillmentStage,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleToCompleteFulfillment,
    handleToCompleteCheckFulfillment,
    handleRejectRequestVerification,
    handleRejectReview,
    handleRejectFollowUp,
    handleRejectCheckFollowUp,
    handleDeCompleteVerification,
    handleSaveBinaryScanComponents,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
  } = useClarityVerification(projectSeq, binaryVerificationSeq, modifiedComponentData);

  const handleSave = () => {
    handleSaveBinaryScanComponents();
    setIsEditMode(false);
  };

  const handleComponentDataChange = useCallback((data) => {
    if (data && data.length > 0) {
      setModifiedComponentData(data);
    }
  }, []);

  // Transform API data to Communication component format
  useEffect(() => {
    if (requestList?.requests) {
      const transformedRequests = requestList.requests.map((request) => {
        // Find requester info from creatorList
        const requesterInfo = creatorList.find((user) => user.value === request.createrId);
        const requesterRole = requesterInfo?.roleCdName || "unknown";

        // Find assignee info from creatorList
        const assigneeInfo = creatorList.find((user) => user.value === request.assigneeId);
        const assigneeRole = assigneeInfo?.roleCdName || "unknown";

        return {
          id: request.actionSeq,
          date: formatFullDateTime(request.createdAt),
          status: request.verificationStatusCdName || "Pending",
          actionStatus: request.actionStatus,
          requester: {
            requesterId: request.createrId,
            name: request.createrName || request.createrId || "Unknown",
            role: requesterRole,
          },
          assignee: {
            assigneeId: request.assigneeId,
            name: request.assigneeName || request.assigneeId || "Unknown",
            role: assigneeRole,
          },
          description: request.actionsContent || "No description provided",
          completionRequestDate: request.completionRequestDt || request.createdAt,
          cve: request.cve,
          conversation:
            request.binaryActionDetails?.map((detail) => {
              // Find user info from creatorList
              const userInfo = creatorList.find((user) => user.value === detail.createrId);
              const userRole = userInfo?.roleCdName || "Unknown";

              return {
                user: {
                  name: userInfo?.userName || userInfo?.label || "Unknown",
                  role: userRole,
                },
                status: detail.actionStatus,
                timestamp: formatFullDateTime(detail.createdAt),
                content: detail.actionContent || "",
              };
            }) || [],
        };
      });

      setRequests(transformedRequests);
    }

    // System messages from action API (CVE + Security Alert digests)
    const cveMessages = requestList?.systemMessages || [];
    setSystemMessages(cveMessages);
  }, [requestList, creatorList]);

  const getCurrentStepIndex = () => {
    const statusStepMap = {
      "Request ReVerification": 0,
      "Analyzing...": 1,
      "Usage Type Review": 2,
      "Usage Type Check": 3,
      "Usage Type Input": 4,
      "Reviewing..": 5,
      "Standby Followup": 6,
      "Finish Folllowup": 7,
      Done: 8,
    };
    return statusStepMap[verificationData.verificationStatusCdName] || 0;
  };

  // Check if any request has actionStatus other than "DONE"
  const shouldShowForceCompleteText = () => {
    const requests = requestList?.requests || [];
    if (requests.length === 0) return false;
    return !requests.every((request) => (request.actionStatus || "").toUpperCase() === "DONE");
  };

  // Workflow steps based on the design - exactly 8 steps as shown in Figma
  const workflowSteps = [
    { label: "Request ReVerification", icon: null, displayLabel: t("fossid-status-options.reverification") }, // No icon for this step
    { label: "Analyzing", icon: AnalysisIcon, displayLabel: t("clarity-status-options.analyzing") },
    { label: "Usage type review", icon: UsageTypeReviewIcon, displayLabel: t("status.usageTypeReview") },
    { label: "Usage type check", icon: UsageTypeCheckIcon, displayLabel: t("status.usageTypeCheck") },
    { label: "Usage type input", icon: UsageTypeInputIcon, displayLabel: t("status.usageTypeInput") },
    { label: "Review", icon: ReviewIcon, displayLabel: t("status.onReview") },
    { label: "Follow-up", icon: FollowUpIcon, displayLabel: t("status.standbyFollowup") },
    { label: "Check follow-up", icon: CheckFollowUpIcon, displayLabel: t("status.followupCheck") },
    { label: "Ready", icon: ReadyIcon, displayLabel: t("status.done") },
  ];

  // Helper function to get request data
  const getRequestData = (requestId, actionContent) => {
    const request = requestList?.requests?.find((req) => req.actionSeq === requestId);

    if (!request) {
      console.error("Request not found:", requestId);
      return null;
    }

    return {
      actionSeq: request.actionSeq,
      binaryVerificationSeq: binaryVerificationSeq,
      actionContent: actionContent,
    };
  };

  // Communication
  const handleComplete = (completeData) => {
    const { requestId, message } = completeData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      completeAction.mutate(requestData);
    }
  };

  const handleApprove = (approvalData) => {
    const { requestId, message } = approvalData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      approveAction.mutate(requestData);
    }
  };

  const handleReject = (rejectionData) => {
    const { requestId, message } = rejectionData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      rejectAction.mutate(requestData);
    }
  };

  const handleAddRequest = (requestData) => {
    // Convert date to ISO format
    const completionRequestDt = new Date(requestData.completionRequestDate).toISOString();

    const requestPayload = {
      assigneeId: requestData.assignee,
      completionRequestDt: completionRequestDt,
      actionsContent: requestData.requestMessage,
      binaryVerificationSeq: binaryVerificationSeq,
      ...(requestData.cve && { cve: requestData.cve }),
      ...(requestData.actionSeq && { actionSeq: requestData.actionSeq }),
    };

    createActionRequest.mutate(requestPayload);
  };

  const handleAddComment = (commentData) => {
    // Check verification status to determine which API to call
    if (verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP") {
      addFulfillmentComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ON_REVIEW") {
      addVerificationComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Check"
    ) {
      addUsageComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Review"
    ) {
      addUsageReviewComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Input"
    ) {
      addUsageInputComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "FOLLOWUP_CHECK") {
      addFollowupCheckComment.mutate(commentData);
    } else {
      console.error("Invalid verification status:", verificationData?.verificationStatusCd);
    }
  };

  return (
    <div className="min-h-full" data-testid="clarity-verification-page">
      {/* Header with Breadcrumbs */}
      <div className="py-4" data-testid="clarity-verification-header">
        <div className="space-y-3">
          <Breadcrumb
            data-testid="clarity-verification-breadcrumb"
            onBackClick={() => {
              navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
            }}
            items={[
              { label: t("navbar.project-list"), href: "/projects" },
              {
                label: t("navbar.project-details"),
                onClick: () => {
                  navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
                },
              },
              { label: t("navbar.clarity-verification") },
            ]}
          />
          <div>
            <h1
              className={`text-2xl font-bold ${colors.textPrimary}`}
              data-testid="clarity-verification-page-title"
            >
              {verificationData.realFileName}
            </h1>
          </div>
        </div>
      </div>

      <div>
        {/* Basic Information */}
        <BasicInfoSection
          data-testid="clarity-verification-basic-info"
          data={{
            ...verificationData,
            projectName: projectData?.projectName || verificationData?.projectName,
          }}
          colors={colors}
          t={t}
          type={"clarity"}
        />

        {/* Workflow Progress */}
        <div className={`px-6 py-6 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid="clarity-verification-workflow">
          <WorkflowProgressClarity
            steps={workflowSteps}
            currentStep={getCurrentStepIndex()}
            projectManagerId={projectData?.projectManagerId}
            onMoveToUsageTypeCheck={handleMoveToUsageTypeCheck}
            onMoveToUsageTypeInput={handleMoveToUsageTypeInput}
            onMoveToReview={handleMoveToReview}
            onMoveToFollowUp={handleMoveToFulfillment}
            onMoveToFollowCheckUp={handleToCompleteFulfillment}
            onCompleteCheckFollowUp={handleToCompleteCheckFulfillment}
            onMoveToDone={handleMoveToDone}
            onRejectUsageTypeCheck={handleRejectUsageTypeCheck}
            onRejectUsageTypeInput={handleRejectUseTypeInput}
            onRejectReview={handleRejectReview}
            onRejectFollowUp={handleRejectFollowUp}
            onRejectCheckFollowUp={handleRejectCheckFollowUp}
            onDeCompleteVerification={handleDeCompleteVerification}
            onRejectRequestVerification={handleRejectRequestVerification}
            onRequestReVerification={handleToRequestReVerification}
            onReUploadFile={handleReUploadFile}
            forceCompleteText={shouldShowForceCompleteText()}
            isSkipProgress={verificationData?.skipProgressFlag}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6 mt-6">
        {/* Communication Section */}
        <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="clarity-verification-communication">
          <Communication
            requests={requests}
            systemMessages={systemMessages}
            verificationData={verificationData}
            verificationType="binary"
            verificationSeq={binaryVerificationSeq}
            onComplete={handleComplete}
            onApprove={handleApprove}
            onReject={handleReject}
            onAddRequest={handleAddRequest}
            onAddComment={handleAddComment}
            isAddCommentPending={
              addVerificationComment.isPending || addFulfillmentComment.isPending || addUsageComment.isPending || addUsageReviewComment.isPending || addUsageInputComment.isPending || addFollowupCheckComment.isPending
            }
            projectMembers={projectMembers}
            projectSeq={projectSeq}
            projectData={projectData}
            creatorList={creatorList}
          />
        </div>

        {/* Component List */}
        <div data-testid="clarity-verification-component-table">
        <ComponentTable
          data={fetchedBinaryScanComponent}
          licensePolicyData={fetchedLicensePolicy}
          onDataChange={handleComponentDataChange}
          isEditingAllowed={verificationStatus && verificationData.verificationStatusCdName === "Usage Type Input"}
          seq="binaryScanComponentSeq"
          onSave={handleSave}
          className="p-4"
          allowVexReview
          showShieldFilter
          vexContext={{
            scanToolCd: "clarity",
            verificationSeq: binaryVerificationSeq,
            projectSeq,
            projectManagerId: projectData?.projectManagerId,
            verificationStatusCd: verificationData?.verificationStatusCd,
          }}
        >
          <ComponentTableHeader
            title={t("common.component-list")}
            count={fetchedBinaryScanComponent?.length || 0}
            colors={colors}
            t={t}
          />
        </ComponentTable>
        </div>

        {/* Fulfillment List */}
        {(isReviewOrDoneStage || isFulfillmentOrCheckFulfillmentStage) && (
          <FulfillmentInfo
            key={binaryScanFollowupPlanUpdatedAt}
            data-testid="clarity-verification-fulfillment-info"
            loggedInDisplayName={creatorList.find((user) => user.userId === getLocalStorage(LOCAL_STORAGE.USER_ID))}
            ref={fulfillmentInfoRef}
            fulfillmentData={fetchedBinaryScanFollowupPlan}
            verificationStatus={verificationStatus}
            projectManagerId={projectData?.projectManagerId}
          />
        )}

        {/* Verification Report */}
        {isNotAnalysis && (
          <VerificationReportInfo
            data-testid="clarity-verification-report-info"
            projectId={projectId}
            projectManagerId={projectData?.projectManagerId}
            verificationVersion={verificationVersion}
            handleGetVerificationReportForPopup={handleGetVerificationReportForPopup}
            verificationReportsData={verificationReportsData}
            verificationSeq={binaryVerificationSeq}
          />
        )}
      </div>
    </div>
  );
};

export default ClarityVerification;
